import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  useGeneralExpenses,
  useGeneralExpenseStats,
  useCreateGeneralExpense,
  useUpdateGeneralExpense,
  useDeleteGeneralExpense,
  useMarkGeneralExpensePaid,
  useExpenseCategories,
  useSupplierCategories,
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useDeleteSuppliers,
  useSupplierInvoices,
  useSupplierInvoiceStats,
  useCreateSupplierInvoice,
  useUpdateSupplierInvoice,
  useDeleteSupplierInvoice,
  useDeleteSupplierInvoices,
  useMarkInvoicePaid,
  useAddInvoicePayment,
  useRecurringExpenses,
  useCreateRecurringExpense,
  useUpdateRecurringExpense,
  useDeleteRecurringExpense,
  useToggleRecurringExpense,
  useGenerateRecurringExpenses,
  expenseKeys,
} from './hooks/useExpenses';
import {
  Container, Typography, Paper, TextField, Button, Grid, Box, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Tabs, Tab, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Divider,
  useMediaQuery, useTheme, TablePagination, CircularProgress
} from '@mui/material';
import {
  Add, Edit, Delete, Business, Receipt, AccountBalance,
  CheckCircle, Pending, Payment, Repeat, PlayArrow, Stop, Visibility,
  EditOutlined, DeleteOutlined, VisibilityOutlined, PaymentOutlined, CheckCircleOutlined,
  TrendingDown, CreditCard, HourglassEmpty, People, Search
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { el } from 'date-fns/locale';
import { fetchWithAuth } from './lib/api';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';
import { AccessDenied } from './components/PermissionGate';
import GeneralExpensesTab from './expenses/GeneralExpensesTab';
import InvoicesTab from './expenses/InvoicesTab';
import RecurringExpensesTab from './expenses/RecurringExpensesTab';
import SuppliersTab from './expenses/SuppliersTab';
import ExpenseDialogs from './expenses/ExpenseDialogs';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 1.5, md: 3 } }}>{children}</Box>}
    </div>
  );
}

export default function Expenses() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Permission checks
  const { can } = usePermissions();
  const canView = can(PERMISSIONS.EXPENSES_VIEW);
  const canCreate = can(PERMISSIONS.EXPENSES_CREATE);
  const canEdit = can(PERMISSIONS.EXPENSES_EDIT);
  const canDelete = can(PERMISSIONS.EXPENSES_DELETE);
  
  // Initialize tabValue from URL, default to 0
  const [tabValue, setTabValue] = useState(() => {
    const tabParam = searchParams.get('tab');
    const parsed = parseInt(tabParam, 10);
    return !isNaN(parsed) && parsed >= 0 && parsed <= 3 ? parsed : 0;
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const queryClient = useQueryClient();

  // Dialog states
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState(''); // Search suppliers by name
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState(''); // Search invoices
  const [supplierForm, setSupplierForm] = useState({
    name: '', vat_number: '', phone: '', email: '', address: '', notes: '', payment_terms: 'credit', category_id: ''
  });
  const [filterSupplierCategory, setFilterSupplierCategory] = useState('');
  const [supplierPeriod, setSupplierPeriod] = useState('12m');
  const [filterSupplierBalance, setFilterSupplierBalance] = useState('');
  const [filterSupplierTurnover, setFilterSupplierTurnover] = useState('');
  const [supplierSort, setSupplierSort] = useState('name');

  const [generalExpenseDialog, setGeneralExpenseDialog] = useState(false);
  const [editingGeneralExpense, setEditingGeneralExpense] = useState(null);
  const [generalExpenseForm, setGeneralExpenseForm] = useState({
    category_id: '',
    amount: '',
    expense_date: dayjs().format('YYYY-MM-DD'), // Use dayjs to avoid timezone issues
    description: '',
    payment_status: 'pending',
    payment_date: '',
    net_amount: '',
    vat_rate: 0, // Default 0% VAT
    vat_amount: '',
    total_amount: '',
    // Recurring expense fields
    is_recurring: false,
    day_of_month: '1',
    recurring_type: 'infinite', // 'infinite' or 'limited'
    total_occurrences: ''
  });

  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    amount: '',
    category_id: '',
    description: '',
    payment_status: 'pending',
    payment_date: '',
    net_amount: '',
    vat_rate: 24,
    vat_amount: '',
    total_amount: '',
    payment_terms: 'credit'
  });

  // Filters - Default to show all data (no date filtering)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1); // 1 year ago to show all historical data
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1); // 1 year ahead for future invoices
    return date;
  });
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination state
  const [generalExpensePage, setGeneralExpensePage] = useState(0);
  const [generalExpenseRowsPerPage, setGeneralExpenseRowsPerPage] = useState(50);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceRowsPerPage, setInvoiceRowsPerPage] = useState(50);
  const [supplierPage, setSupplierPage] = useState(0);
  const [supplierRowsPerPage, setSupplierRowsPerPage] = useState(50);

  const [recurringDialog, setRecurringDialog] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [recurringForm, setRecurringForm] = useState({
    category_id: '',
    amount: '',
    description: '',
    frequency: 'monthly',
    day_of_month: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    active: 1
  });

  // Invoice Preview Modal State
  const [invoicePreviewDialog, setInvoicePreviewDialog] = useState(false);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);

  // Invoice Payment Dialog
  const [invoicePaymentDialog, setInvoicePaymentDialog] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [invoicePaymentForm, setInvoicePaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  // General Expense Payment Dialog
  const [expensePaymentDialog, setExpensePaymentDialog] = useState(false);
  const [paymentExpense, setPaymentExpense] = useState(null);
  const [expensePaymentForm, setExpensePaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });

  // Payment History Dialog for General Expenses
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(false);
  const [paymentHistoryExpense, setPaymentHistoryExpense] = useState(null);
  const [paymentHistoryData, setPaymentHistoryData] = useState([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);

  // myDATA sync state
  const [syncing, setSyncing] = useState(false);

  // Filters for queries - use dayjs to avoid timezone issues (MUST be defined before hooks that use them)
  const generalExpenseFilters = useMemo(() => ({
    startDate: dayjs(startDate).format('YYYY-MM-DD'),
    endDate: dayjs(endDate).format('YYYY-MM-DD'),
    ...(filterCategory && { category_id: filterCategory }),
    ...(filterStatus && { payment_status: filterStatus }),
    page: generalExpensePage + 1, // API uses 1-based pages
    limit: generalExpenseRowsPerPage,
  }), [startDate, endDate, filterCategory, filterStatus, generalExpensePage, generalExpenseRowsPerPage]);

  const invoiceFilters = useMemo(() => ({
    startDate: dayjs(startDate).format('YYYY-MM-DD'),
    endDate: dayjs(endDate).format('YYYY-MM-DD'),
    ...(filterSupplier && { supplier_id: filterSupplier }),
    ...(filterCategory && { category_id: filterCategory }),
    ...(filterStatus && { payment_status: filterStatus }),
    page: invoicePage + 1,
    limit: invoiceRowsPerPage,
  }), [startDate, endDate, filterSupplier, filterCategory, filterStatus, invoicePage, invoiceRowsPerPage]);

  const supplierTurnoverRange = useMemo(() => {
    if (supplierPeriod === 'all') return {};
    const end = dayjs();
    let start;
    switch (supplierPeriod) {
      case 'month':
        start = end.startOf('month');
        break;
      case '3m':
        start = end.subtract(3, 'month');
        break;
      case '6m':
        start = end.subtract(6, 'month');
        break;
      case '12m':
      default:
        start = end.subtract(12, 'month');
        break;
    }
    return {
      turnover_from: start.format('YYYY-MM-DD'),
      turnover_to: end.format('YYYY-MM-DD'),
    };
  }, [supplierPeriod]);

  const supplierSortParams = useMemo(() => {
    switch (supplierSort) {
      case 'balance_desc':
        return { sort_by: 'balance', sort_dir: 'desc' };
      case 'turnover_desc':
        return { sort_by: 'turnover', sort_dir: 'desc' };
      default:
        return { sort_by: 'name', sort_dir: 'asc' };
    }
  }, [supplierSort]);

  const supplierFilters = useMemo(() => ({
    page: supplierPage + 1,
    limit: supplierRowsPerPage,
    ...(supplierSearchTerm && { search: supplierSearchTerm }),
    ...(filterSupplierCategory && { category_id: filterSupplierCategory }),
    ...(filterSupplierBalance && { balance_status: filterSupplierBalance }),
    ...(filterSupplierTurnover && { turnover_status: filterSupplierTurnover }),
    ...supplierTurnoverRange,
    ...supplierSortParams,
  }), [
    supplierPage,
    supplierRowsPerPage,
    supplierSearchTerm,
    filterSupplierCategory,
    filterSupplierBalance,
    filterSupplierTurnover,
    supplierTurnoverRange,
    supplierSortParams,
  ]);

  // React Query hooks - Data fetching
  // Suppliers with pagination for tab display
  const { data: suppliersResponse = { data: [], pagination: { total: 0 } }, isLoading: suppliersLoading } = useSuppliers(
    tabValue === 2 ? supplierFilters : { limit: 500 } // For dropdowns, get more suppliers
  );
  const suppliers = suppliersResponse.data || suppliersResponse || []; // Handle both formats
  const suppliersPagination = suppliersResponse.pagination || { total: Array.isArray(suppliersResponse) ? suppliersResponse.length : 0 };
  const suppliersSummary = suppliersResponse.summary || { total_balance: 0, total_period_turnover: 0, supplier_count: 0 };
  const { data: categories = [], isLoading: categoriesLoading } = useExpenseCategories();
  const { data: supplierCategories = [] } = useSupplierCategories();
  const { data: recurringExpenses = [], isLoading: recurringLoading } = useRecurringExpenses();
  
  // Suppliers are now filtered server-side via supplierFilters.search
  const filteredSuppliers = suppliers;

  const { data: generalExpensesResponse = { data: [], pagination: { total: 0 } }, isLoading: generalExpensesLoading, refetch: refetchGeneralExpenses } = useGeneralExpenses(
    tabValue === 0 ? generalExpenseFilters : {}
  );
  const generalExpenses = generalExpensesResponse.data || [];
  const generalExpensesPagination = generalExpensesResponse.pagination || { total: 0 };
  
  // Debug: Log when generalExpenses changes - DISABLED to prevent console spam
  // useEffect(() => {
  //   if (tabValue === 0) {
  //     console.log('📊 General expenses updated:', generalExpenses.length, 'items');
  //   }
  // }, [generalExpenses.length, tabValue]);
  const { data: generalStats = { total_amount: 0, paid_amount: 0, pending_amount: 0 }, isLoading: generalStatsLoading } = useGeneralExpenseStats(
    tabValue === 0 ? { startDate: dayjs(startDate).format('YYYY-MM-DD'), endDate: dayjs(endDate).format('YYYY-MM-DD'), ...(filterCategory && { category_id: filterCategory }), ...(filterStatus && { payment_status: filterStatus }) } : {}
  );
  const { data: invoicesResponse = { data: [], pagination: { total: 0 } }, isLoading: invoicesLoading } = useSupplierInvoices(
    tabValue === 1 ? invoiceFilters : {}
  );
  const invoices = invoicesResponse.data || [];
  const invoicesPagination = invoicesResponse.pagination || { total: 0 };
  const { data: invoiceStats = { total_amount: 0, paid_amount: 0, pending_amount: 0 }, isLoading: invoiceStatsLoading } = useSupplierInvoiceStats(
    tabValue === 1 ? { startDate: dayjs(startDate).format('YYYY-MM-DD'), endDate: dayjs(endDate).format('YYYY-MM-DD'), ...(filterSupplier && { supplier_id: filterSupplier }), ...(filterCategory && { category_id: filterCategory }), ...(filterStatus && { payment_status: filterStatus }) } : {}
  );

  // Filter invoices by search term (supplier name, invoice number, description)
  const filteredInvoices = useMemo(() => {
    if (!invoiceSearchTerm.trim()) return invoices;
    const searchLower = invoiceSearchTerm.toLowerCase().trim();
    return invoices.filter(inv => 
      (inv.supplier_name || '').toLowerCase().includes(searchLower) ||
      (inv.invoice_number || '').toLowerCase().includes(searchLower) ||
      (inv.description || '').toLowerCase().includes(searchLower)
    );
  }, [invoices, invoiceSearchTerm]);

  // Mutations
  const createGeneralExpenseMutation = useCreateGeneralExpense();
  const updateGeneralExpenseMutation = useUpdateGeneralExpense();
  const deleteGeneralExpenseMutation = useDeleteGeneralExpense();
  const markPaidMutation = useMarkGeneralExpensePaid();
  const createSupplierMutation = useCreateSupplier();
  const updateSupplierMutation = useUpdateSupplier();
  const deleteSupplierMutation = useDeleteSupplier();
  const deleteSuppliersMutation = useDeleteSuppliers();
  const createInvoiceMutation = useCreateSupplierInvoice();
  const updateInvoiceMutation = useUpdateSupplierInvoice();
  const deleteInvoiceMutation = useDeleteSupplierInvoice();
  const deleteInvoicesMutation = useDeleteSupplierInvoices();
  const markInvoicePaidMutation = useMarkInvoicePaid();
  const addInvoicePaymentMutation = useAddInvoicePayment();

  // Reset pagination when filters change
  useEffect(() => {
    setGeneralExpensePage(0);
  }, [startDate, endDate, filterCategory, filterStatus]);

  useEffect(() => {
    setInvoicePage(0);
  }, [startDate, endDate, filterSupplier, filterCategory, filterStatus]);

  useEffect(() => {
    setSupplierPage(0);
  }, [supplierSearchTerm]);
  const createRecurringMutation = useCreateRecurringExpense();
  const updateRecurringMutation = useUpdateRecurringExpense();
  const deleteRecurringMutation = useDeleteRecurringExpense();
  const toggleRecurringMutation = useToggleRecurringExpense();
  const generateRecurringMutation = useGenerateRecurringExpenses();

  // Auto-generate recurring expenses on component mount (runs once)
  const hasGeneratedRef = React.useRef(false);
  useEffect(() => {
    if (!hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      generateRecurringMutation.mutate();
    }
  }, [generateRecurringMutation]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  // ========== GENERAL EXPENSES ==========
  const handleSaveGeneralExpense = async () => {
    try {
      if (editingGeneralExpense) {
        // Optimistic update for edit
        const currentResponse = queryClient.getQueryData(['expenses', 'general', generalExpenseFilters]) || { data: [], pagination: { total: 0 } };
        const currentData = currentResponse.data || [];
        const expenseIndex = currentData.findIndex(e => e.id === editingGeneralExpense.id);
        
        if (expenseIndex !== -1) {
          // Fetch category name
          const category = categories?.find(c => c.id === parseInt(generalExpenseForm.category_id));
          
          // Create updated expense object
          const updatedExpense = {
            ...currentData[expenseIndex],
            category_id: parseInt(generalExpenseForm.category_id),
            amount: parseFloat(generalExpenseForm.total_amount || generalExpenseForm.amount),
            expense_date: generalExpenseForm.expense_date,
            description: generalExpenseForm.description || null,
            payment_status: generalExpenseForm.payment_status || 'pending',
            payment_date: generalExpenseForm.payment_date || null,
            net_amount: generalExpenseForm.net_amount ? parseFloat(generalExpenseForm.net_amount) : null,
            vat_rate: generalExpenseForm.vat_rate !== '' && generalExpenseForm.vat_rate !== null && generalExpenseForm.vat_rate !== undefined ? parseFloat(generalExpenseForm.vat_rate) : 24,
            vat_amount: generalExpenseForm.vat_amount ? parseFloat(generalExpenseForm.vat_amount) : null,
            total_amount: generalExpenseForm.total_amount ? parseFloat(generalExpenseForm.total_amount) : parseFloat(generalExpenseForm.amount),
            category_name: category?.name || null
          };
          
          // Update in array
          const updatedData = [...currentData];
          updatedData[expenseIndex] = updatedExpense;
          
          // Update cache immediately
          queryClient.setQueryData(
            ['expenses', 'general', generalExpenseFilters],
            { ...currentResponse, data: updatedData }
          );
          
          console.log('✅ Cache updated with edited expense');
        }
        
        // Prepare clean data for API
        const cleanData = {
          ...generalExpenseForm,
          category_id: parseInt(generalExpenseForm.category_id),
          amount: parseFloat(generalExpenseForm.total_amount || generalExpenseForm.amount) || 0,
          payment_date: generalExpenseForm.payment_date || null,
          net_amount: generalExpenseForm.net_amount ? parseFloat(generalExpenseForm.net_amount) : null,
          vat_rate: generalExpenseForm.vat_rate !== '' && generalExpenseForm.vat_rate !== null && generalExpenseForm.vat_rate !== undefined ? parseFloat(generalExpenseForm.vat_rate) : 24,
          vat_amount: generalExpenseForm.vat_amount ? parseFloat(generalExpenseForm.vat_amount) : null,
          total_amount: generalExpenseForm.total_amount ? parseFloat(generalExpenseForm.total_amount) : null
        };
        
        await updateGeneralExpenseMutation.mutateAsync({
          id: editingGeneralExpense.id,
          data: cleanData
        });
        showSnackbar('Το έξοδο ενημερώθηκε!', 'success');
        
        // Reset ALL expense queries to force fresh data (including stats)
        await queryClient.resetQueries({ queryKey: expenseKeys.all });
      } else {
        // Prepare clean data for API
        const cleanData = {
          ...generalExpenseForm,
          category_id: parseInt(generalExpenseForm.category_id),
          amount: parseFloat(generalExpenseForm.total_amount || generalExpenseForm.amount) || 0,
          payment_date: generalExpenseForm.payment_date || null,
          net_amount: generalExpenseForm.net_amount ? parseFloat(generalExpenseForm.net_amount) : null,
          vat_rate: generalExpenseForm.vat_rate !== '' && generalExpenseForm.vat_rate !== null && generalExpenseForm.vat_rate !== undefined ? parseFloat(generalExpenseForm.vat_rate) : 24,
          vat_amount: generalExpenseForm.vat_amount ? parseFloat(generalExpenseForm.vat_amount) : null,
          total_amount: generalExpenseForm.total_amount ? parseFloat(generalExpenseForm.total_amount) : null
        };
        
        const result = await createGeneralExpenseMutation.mutateAsync(cleanData);
        console.log('✅ Expense created:', result);
        
        // If recurring is checked, also create a recurring expense for future months
        if (generalExpenseForm.is_recurring) {
          try {
            const recurringData = {
              category_id: parseInt(generalExpenseForm.category_id),
              amount: parseFloat(generalExpenseForm.total_amount || generalExpenseForm.amount) || 0,
              description: generalExpenseForm.description || '',
              frequency: 'monthly',
              day_of_month: parseInt(generalExpenseForm.day_of_month) || 1,
              start_date: generalExpenseForm.expense_date,
              net_amount: generalExpenseForm.net_amount ? parseFloat(generalExpenseForm.net_amount) : null,
              vat_rate: generalExpenseForm.vat_rate !== '' && generalExpenseForm.vat_rate !== null && generalExpenseForm.vat_rate !== undefined ? parseFloat(generalExpenseForm.vat_rate) : 24,
              vat_amount: generalExpenseForm.vat_amount ? parseFloat(generalExpenseForm.vat_amount) : null,
              total_amount: generalExpenseForm.total_amount ? parseFloat(generalExpenseForm.total_amount) : null,
              total_occurrences: generalExpenseForm.recurring_type === 'limited' && generalExpenseForm.total_occurrences 
                ? parseInt(generalExpenseForm.total_occurrences) 
                : null
            };
            
            await createRecurringMutation.mutateAsync(recurringData);
            console.log('✅ Recurring expense template created');
          } catch (recurringError) {
            console.error('Error creating recurring expense:', recurringError);
          }
        }
        
        showSnackbar(generalExpenseForm.is_recurring 
          ? 'Το έξοδο προστέθηκε και θα επαναλαμβάνεται!' 
          : 'Το έξοδο προστέθηκε!', 'success');
      }
      setGeneralExpenseDialog(false);
      setEditingGeneralExpense(null);
      setGeneralExpenseForm({
        category_id: '',
        amount: '',
        expense_date: dayjs().format('YYYY-MM-DD'), // Use dayjs to avoid timezone issues
        description: '',
        payment_status: 'pending',
        payment_date: '',
        net_amount: '',
        vat_rate: 0, // Default 0% VAT
        vat_amount: '',
        total_amount: '',
        // Reset recurring fields
        is_recurring: false,
        day_of_month: '1',
        recurring_type: 'infinite',
        total_occurrences: ''
      });
    } catch (error) {
      console.error('Error saving expense:', error);
      const errorMessage = error.message || error.toString();
      showSnackbar(`Σφάλμα αποθήκευσης εξόδου: ${errorMessage}`, 'error');
    }
  };

  const handleDeleteGeneralExpense = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το έξοδο;')) return;
    try {
      // Optimistic update for delete - remove from cache immediately
      if (tabValue === 0) {
        const currentResponse = queryClient.getQueryData(['expenses', 'general', generalExpenseFilters]) || { data: [], pagination: { total: 0 } };
        const currentData = currentResponse.data || [];
        const updatedData = currentData.filter(e => e.id !== id);
        
        // Update cache immediately
        queryClient.setQueryData(
          ['expenses', 'general', generalExpenseFilters],
          {
            ...currentResponse,
            data: updatedData,
            pagination: { ...currentResponse.pagination, total: Math.max(0, (currentResponse.pagination?.total || 0) - 1) }
          }
        );
        
        console.log('✅ Cache updated, removed expense:', id);
      }
      
      await deleteGeneralExpenseMutation.mutateAsync(id);
      showSnackbar('Το έξοδο διαγράφηκε!', 'success');
      
      // Invalidate all expense queries to ensure stats are updated
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    } catch (error) {
      // On error, refetch to restore correct state
      if (tabValue === 0) {
        await refetchGeneralExpenses();
      }
      showSnackbar('Σφάλμα διαγραφής εξόδου', 'error');
    }
  };

  const handleMarkGeneralExpensePaid = async (id) => {
    try {
      // Optimistic update for mark as paid
      if (tabValue === 0) {
        const currentResponse = queryClient.getQueryData(['expenses', 'general', generalExpenseFilters]) || { data: [], pagination: { total: 0 } };
        const currentData = currentResponse.data || [];
        const expenseIndex = currentData.findIndex(e => e.id === id);
        
        if (expenseIndex !== -1) {
          const updatedData = [...currentData];
          updatedData[expenseIndex] = {
            ...updatedData[expenseIndex],
            payment_status: 'paid',
            payment_date: dayjs().format('YYYY-MM-DD')
          };
          
          queryClient.setQueryData(['expenses', 'general', generalExpenseFilters], { ...currentResponse, data: updatedData });
          console.log('✅ Cache updated, marked expense as paid');
        }
      }
      
      await markPaidMutation.mutateAsync(id);
      showSnackbar('Το έξοδο σημειώθηκε ως πληρωμένο!', 'success');
      
      // Invalidate all expense queries to refresh everywhere
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    } catch (error) {
      // On error, refetch to restore correct state
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
      showSnackbar('Σφάλμα ενημέρωσης εξόδου', 'error');
    }
  };

  // ========== SUPPLIERS ==========
  const handleSaveSupplier = async () => {
    try {
      // Resolve full category object for optimistic UI
      const resolvedCategory = supplierForm.category_id
        ? supplierCategories.find(c => c.id === supplierForm.category_id) || null
        : null;

      const cleanData = {
        name: supplierForm.name || '',
        vat_number: supplierForm.vat_number,
        phone: supplierForm.phone || '',
        email: supplierForm.email || '',
        address: supplierForm.address || '',
        notes: supplierForm.notes || '',
        payment_terms: supplierForm.payment_terms || 'credit',
        category_id: supplierForm.category_id || null
      };

      if (editingSupplier) {
        const editId = editingSupplier.id;

        // Close modal immediately (optimistic UI)
        setSupplierDialog(false);
        setEditingSupplier(null);
        setSupplierForm({ name: '', vat_number: '', phone: '', email: '', address: '', notes: '', payment_terms: 'credit', category_id: '' });
        showSnackbar('Ο προμηθευτής ενημερώθηκε!', 'success');

        // Fire API call then refetch for fresh data with relations
        updateSupplierMutation.mutate(
          { id: editId, data: cleanData },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['expenses', 'suppliers'] });
            },
            onError: (error) => {
              queryClient.invalidateQueries({ queryKey: ['expenses', 'suppliers'] });
              showSnackbar(error?.message || 'Σφάλμα αποθήκευσης προμηθευτή', 'error');
            }
          }
        );
      } else {
        const result = await createSupplierMutation.mutateAsync(cleanData);
        
        showSnackbar('Ο προμηθευτής προστέθηκε!', 'success');
        setSupplierDialog(false);
        setEditingSupplier(null);
        setSupplierForm({ name: '', vat_number: '', phone: '', email: '', address: '', notes: '', payment_terms: 'credit', category_id: '' });

        // Force refetch to get full data with category relation from server
        queryClient.invalidateQueries({ queryKey: ['expenses', 'suppliers'] });
      }
    } catch (error) {
      // On error, refetch to restore correct state
      queryClient.invalidateQueries({ queryKey: ['expenses', 'suppliers'] });
      showSnackbar(error?.message || 'Σφάλμα αποθήκευσης προμηθευτή', 'error');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον προμηθευτή;')) return;
    try {
      // Optimistic update for delete
      const currentData = queryClient.getQueryData(['expenses', 'suppliers']) || [];
      const updatedData = currentData.filter(s => s.id !== id);
      
      queryClient.setQueryData(['expenses', 'suppliers'], updatedData);
      console.log('✅ Cache updated, removed supplier:', id);
      
      await deleteSupplierMutation.mutateAsync(id);
      showSnackbar('Ο προμηθευτής διαγράφηκε!', 'success');
    } catch (error) {
      // On error, refetch to restore correct state
      queryClient.invalidateQueries({ queryKey: ['expenses', 'suppliers'] });
      showSnackbar('Σφάλμα διαγραφής προμηθευτή', 'error');
    }
  };

  const handleSelectSupplier = (id) => {
    setSelectedSuppliers(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllSuppliers = (event) => {
    if (event.target.checked) {
      setSelectedSuppliers(filteredSuppliers.map(sup => sup.id));
    } else {
      setSelectedSuppliers([]);
    }
  };

  const handleBulkDeleteSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      return showSnackbar('Δεν έχετε επιλέξει προμηθευτές', 'warning');
    }
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε ${selectedSuppliers.length} προμηθευτές;`)) {
      return;
    }
    try {
      await deleteSuppliersMutation.mutateAsync(selectedSuppliers);
      showSnackbar(`Διαγράφηκαν ${selectedSuppliers.length} προμηθευτές!`, 'success');
      setSelectedSuppliers([]);
    } catch (error) {
      showSnackbar('Σφάλμα μαζικής διαγραφής προμηθευτών', 'error');
    }
  };

  // ========== INVOICES ==========
  // Data is fetched via React Query hooks above

  const handleSyncMydata = async () => {
    try {
      setSyncing(true);
      const response = await fetchWithAuth('/api/mydata/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      if (data.success) {
        const recon = data.reconciliation;
        let message;
        if (recon?.dateFrom && recon?.dateTo) {
          const fmt = (s) => s.split('-').reverse().join('/');
          const mode = recon.incremental ? 'ενημερωτικό' : 'πλήρες';
          message = `Εισήχθησαν ${data.imported} νέα (${fmt(recon.dateFrom)} – ${fmt(recon.dateTo)}, ${mode}). ${data.skipped || 0} ήδη υπήρχαν.`;
        } else {
          message = `Συγχρονισμός ολοκληρώθηκε! Εισήχθησαν ${data.imported} από ${data.total} τιμολόγια.`;
        }
        showSnackbar(message, 'success');
        // React Query will auto-refetch
        queryClient.invalidateQueries({ queryKey: ['expenses'] });
      } else {
        showSnackbar(data.error || 'Σφάλμα συγχρονισμού', 'error');
      }
    } catch (error) {
      console.error('Sync error:', error);
      showSnackbar('Σφάλμα συγχρονισμού με myDATA', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateSupplierNames = async () => {
    try {
      setSyncing(true);
      showSnackbar('Ενημέρωση ονομάτων προμηθευτών...', 'info');
      
      const response = await fetchWithAuth('/api/mydata/update-supplier-names', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      if (data.success) {
        showSnackbar(
          `Ενημερώθηκαν ${data.updated} από ${data.total} προμηθευτές.`,
          'success'
        );
        // React Query will auto-refetch
        queryClient.invalidateQueries({ queryKey: ['expenses'] });
      } else {
        showSnackbar(data.error || 'Σφάλμα ενημέρωσης', 'error');
      }
    } catch (error) {
      console.error('Update names error:', error);
      showSnackbar('Σφάλμα ενημέρωσης ονομάτων', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveInvoice = async () => {
    try {
      let responseData;
      if (editingInvoice) {
        // Optimistic update for edit
        if (tabValue === 1) {
          const currentData = queryClient.getQueryData(['expenses', 'invoices', invoiceFilters]) || [];
          const invoiceIndex = currentData.findIndex(i => i.id === editingInvoice.id);
          
          if (invoiceIndex !== -1) {
            const supplier = suppliers?.find(s => s.id === parseInt(invoiceForm.supplier_id));
            const category = categories?.find(c => c.id === parseInt(invoiceForm.category_id));
            
            const updatedInvoice = {
              ...currentData[invoiceIndex],
              supplier_id: parseInt(invoiceForm.supplier_id),
              invoice_number: invoiceForm.invoice_number,
              invoice_date: invoiceForm.invoice_date,
              amount: parseFloat(invoiceForm.total_amount || invoiceForm.amount),
              category_id: parseInt(invoiceForm.category_id),
              description: invoiceForm.description || null,
              payment_status: invoiceForm.payment_status || 'pending',
              payment_date: invoiceForm.payment_date || null,
              net_amount: invoiceForm.net_amount ? parseFloat(invoiceForm.net_amount) : null,
              vat_rate: invoiceForm.vat_rate !== '' && invoiceForm.vat_rate !== null && invoiceForm.vat_rate !== undefined ? parseFloat(invoiceForm.vat_rate) : 24,
              vat_amount: invoiceForm.vat_amount ? parseFloat(invoiceForm.vat_amount) : null,
              total_amount: invoiceForm.total_amount ? parseFloat(invoiceForm.total_amount) : parseFloat(invoiceForm.amount),
              payment_terms: invoiceForm.payment_terms || 'credit',
              supplier_name: supplier?.name || null,
              category_name: category?.name || null
            };
            
            const updatedData = [...currentData];
            updatedData[invoiceIndex] = updatedInvoice;
            
            queryClient.setQueryData(['expenses', 'invoices', invoiceFilters], updatedData);
            console.log('✅ Cache updated with edited invoice');
          }
        }
        
        responseData = await updateInvoiceMutation.mutateAsync({
          id: editingInvoice.id,
          data: invoiceForm
        });
        showSnackbar('Το τιμολόγιο ενημερώθηκε!', 'success');
      } else {
        responseData = await createInvoiceMutation.mutateAsync(invoiceForm);
        
        // Optimistic update for create
        if (tabValue === 1) {
          const isInRange = invoiceForm.invoice_date >= invoiceFilters.startDate && 
                           invoiceForm.invoice_date <= invoiceFilters.endDate;
          
          if (isInRange) {
            const currentData = queryClient.getQueryData(['expenses', 'invoices', invoiceFilters]) || [];
            const supplier = suppliers?.find(s => s.id === parseInt(invoiceForm.supplier_id));
            const category = categories?.find(c => c.id === parseInt(invoiceForm.category_id));
            
            const newInvoice = {
              id: responseData.id,
              supplier_id: parseInt(invoiceForm.supplier_id),
              invoice_number: invoiceForm.invoice_number,
              invoice_date: invoiceForm.invoice_date,
              amount: parseFloat(invoiceForm.total_amount || invoiceForm.amount),
              category_id: parseInt(invoiceForm.category_id),
              description: invoiceForm.description || null,
              payment_status: invoiceForm.payment_status || 'pending',
              payment_date: invoiceForm.payment_date || null,
              net_amount: invoiceForm.net_amount ? parseFloat(invoiceForm.net_amount) : null,
              vat_rate: invoiceForm.vat_rate !== '' && invoiceForm.vat_rate !== null && invoiceForm.vat_rate !== undefined ? parseFloat(invoiceForm.vat_rate) : 24,
              vat_amount: invoiceForm.vat_amount ? parseFloat(invoiceForm.vat_amount) : null,
              total_amount: invoiceForm.total_amount ? parseFloat(invoiceForm.total_amount) : parseFloat(invoiceForm.amount),
              payment_terms: invoiceForm.payment_terms || 'credit',
              supplier_name: supplier?.name || null,
              category_name: category?.name || null
            };
            
            const updatedData = [newInvoice, ...currentData];
            
            queryClient.setQueryData(['expenses', 'invoices', invoiceFilters], updatedData);
            console.log('✅ Cache updated with new invoice');
          }
        }
        
        let message = 'Το τιμολόγιο προστέθηκε!';
        if (responseData?.auto_payment) {
          message += ' Δημιουργήθηκε αυτόματα πληρωμή μετρητών.';
        }
      showSnackbar(message, 'success');
      }
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['expenses', 'invoiceStats'] });
      
      setInvoiceDialog(false);
      setEditingInvoice(null);
      setInvoiceForm({
        supplier_id: '',
        invoice_number: '',
        invoice_date: dayjs().format('YYYY-MM-DD'),
        amount: '',
        category_id: '',
        description: '',
        payment_status: 'pending',
        payment_date: '',
        net_amount: '',
        vat_rate: 24,
        vat_amount: '',
        total_amount: '',
        payment_terms: 'credit'
      });
    } catch (error) {
      // On error, refetch to restore correct state
      if (tabValue === 1) {
        queryClient.invalidateQueries({ queryKey: ['expenses', 'invoices'] });
      }
      showSnackbar('Σφάλμα αποθήκευσης τιμολογίου', 'error');
    }
  };

  const handleSelectInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllInvoices = (event) => {
    if (event.target.checked) {
      setSelectedInvoices((filteredInvoices || []).map(inv => inv.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  // Open Invoice Preview Modal
  const handleOpenInvoicePreview = async (invoice) => {
    setInvoicePreviewLoading(true);
    setInvoicePreviewDialog(true);
    
    // Set initial data from what we have
    const initialData = {
      invoice: {
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        invoice_date: invoice.invoice_date,
        supplier_name: invoice.supplier?.name || invoice.supplier_name || 'Άγνωστος',
        supplier_vat: invoice.supplier?.vat_number || '-',
        net_amount: invoice.net_amount,
        vat_amount: invoice.vat_amount,
        vat_rate: invoice.vat_rate,
        total_amount: invoice.total_amount || invoice.amount,
        description: invoice.description,
        payment_status: invoice.payment_status,
        mydata_mark: invoice.mydata_mark
      },
      items: []
    };
    setInvoicePreviewData(initialData);
    
    // Try to fetch items from backend
    if (invoice.mydata_mark) {
      try {
        const response = await fetchWithAuth(`/api/mydata/supplier-invoices/${invoice.id}/items`);
        if (response.ok) {
          const data = await response.json();
          
          // If no items and not fetched yet, try to fetch from myDATA
          if ((!data.items || data.items.length === 0) && !data.invoice?.items_fetched) {
            try {
              const fetchResponse = await fetchWithAuth(`/api/mydata/fetch-invoice-items/${invoice.id}`, {
                method: 'POST'
              });
              if (fetchResponse.ok) {
                const fetchData = await fetchResponse.json();
                if (fetchData.items && fetchData.items.length > 0) {
                  setInvoicePreviewData(prev => ({ ...prev, items: fetchData.items }));
                }
              }
            } catch (fetchErr) {
              console.log('Could not fetch items from myDATA:', fetchErr);
            }
          } else if (data.items && data.items.length > 0) {
            setInvoicePreviewData(prev => ({ ...prev, items: data.items }));
          }
        }
      } catch (error) {
        console.log('Error fetching invoice items:', error);
      }
    }
    
    setInvoicePreviewLoading(false);
  };

  const handleBulkDeleteInvoices = async () => {
    if (selectedInvoices.length === 0) {
      showSnackbar('Δεν έχετε επιλέξει τιμολόγια', 'warning');
      return;
    }
    
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε ${selectedInvoices.length} τιμολόγια;`)) return;
    
    try {
      await deleteInvoicesMutation.mutateAsync(selectedInvoices);
      showSnackbar(`Διαγράφηκαν ${selectedInvoices.length} τιμολόγια!`, 'success');
      setSelectedInvoices([]);
    } catch (error) {
      showSnackbar('Σφάλμα μαζικής διαγραφής', 'error');
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το τιμολόγιο;')) return;
    try {
      // Optimistic update for delete
      if (tabValue === 1) {
        const currentData = queryClient.getQueryData(['expenses', 'invoices', invoiceFilters]) || [];
        const updatedData = currentData.filter(i => i.id !== id);
        
        queryClient.setQueryData(['expenses', 'invoices', invoiceFilters], updatedData);
        console.log('✅ Cache updated, removed invoice:', id);
      }
      
      await deleteInvoiceMutation.mutateAsync(id);
      showSnackbar('Το τιμολόγιο διαγράφηκε!', 'success');
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['expenses', 'invoiceStats'] });
    } catch (error) {
      // On error, refetch to restore correct state
      if (tabValue === 1) {
        queryClient.invalidateQueries({ queryKey: ['expenses', 'invoices'] });
      }
      showSnackbar('Σφάλμα διαγραφής τιμολογίου', 'error');
    }
  };

  const handleMarkInvoicePaid = async (id) => {
    try {
      // Optimistic update for mark as paid
      if (tabValue === 1) {
        const currentData = queryClient.getQueryData(['expenses', 'invoices', invoiceFilters]) || [];
        const invoiceIndex = currentData.findIndex(i => i.id === id);
        
        if (invoiceIndex !== -1) {
          const updatedData = [...currentData];
          updatedData[invoiceIndex] = {
            ...updatedData[invoiceIndex],
            payment_status: 'paid',
            payment_date: dayjs().format('YYYY-MM-DD')
          };
          
          queryClient.setQueryData(['expenses', 'invoices', invoiceFilters], updatedData);
          console.log('✅ Cache updated, marked invoice as paid');
        }
      }
      
      await markInvoicePaidMutation.mutateAsync(id);
      showSnackbar('Το τιμολόγιο σημειώθηκε ως πληρωμένο!', 'success');
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['expenses', 'invoiceStats'] });
    } catch (error) {
      // On error, refetch to restore correct state
      if (tabValue === 1) {
        queryClient.invalidateQueries({ queryKey: ['expenses', 'invoices'] });
      }
      showSnackbar('Σφάλμα ενημέρωσης τιμολογίου', 'error');
    }
  };

  const handleOpenInvoicePayment = (invoice) => {
    setPaymentInvoice(invoice);
    const balanceDue = invoice.total_amount - (invoice.paid_amount || 0);
    setInvoicePaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: balanceDue.toFixed(2),
      payment_method: 'bank_transfer',
      reference: invoice.invoice_number,
      notes: ''
    });
    setInvoicePaymentDialog(true);
  };

  const handleCloseInvoicePayment = () => {
    setInvoicePaymentDialog(false);
    setPaymentInvoice(null);
    setInvoicePaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'bank_transfer',
      reference: '',
      notes: ''
    });
  };

  const handleSaveInvoicePayment = async () => {
    if (!invoicePaymentForm.amount || parseFloat(invoicePaymentForm.amount) <= 0) {
      return showSnackbar('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
    }

    try {
      // Optimistic update for invoice payment
      if (tabValue === 1) {
        const currentData = queryClient.getQueryData(['expenses', 'invoices', invoiceFilters]) || [];
        const invoiceIndex = currentData.findIndex(i => i.id === paymentInvoice.id);
        
        if (invoiceIndex !== -1) {
          const updatedData = [...currentData];
          const paymentAmount = parseFloat(invoicePaymentForm.amount);
          const currentPaid = parseFloat(updatedData[invoiceIndex].paid_amount || 0);
          const invoiceTotal = parseFloat(updatedData[invoiceIndex].total_amount);
          const newPaid = currentPaid + paymentAmount;
          
          updatedData[invoiceIndex] = {
            ...updatedData[invoiceIndex],
            paid_amount: newPaid,
            payment_status: newPaid >= invoiceTotal ? 'paid' : (newPaid > 0 ? 'partial' : 'pending'),
            payment_date: newPaid >= invoiceTotal ? invoicePaymentForm.payment_date : updatedData[invoiceIndex].payment_date
          };
          
          queryClient.setQueryData(['expenses', 'invoices', invoiceFilters], updatedData);
          console.log('✅ Cache updated with invoice payment');
        }
      }
      
      await addInvoicePaymentMutation.mutateAsync({
        invoiceId: paymentInvoice.id,
        data: {
          supplier_id: paymentInvoice.supplier_id,
          ...invoicePaymentForm,
          amount: parseFloat(invoicePaymentForm.amount)
        }
      });
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: ['expenses', 'invoiceStats'] });
      
        showSnackbar('Η πληρωμή καταχωρήθηκε επιτυχώς!', 'success');
        handleCloseInvoicePayment();
    } catch (error) {
      // On error, refetch to restore correct state
      if (tabValue === 1) {
        queryClient.invalidateQueries({ queryKey: ['expenses', 'invoices'] });
      }
      showSnackbar(`Σφάλμα αποθήκευσης πληρωμής: ${error.message || error}`, 'error');
    }
  };

  // ========== GENERAL EXPENSE PAYMENT HANDLERS ==========
  const handleOpenExpensePayment = (expense) => {
    setPaymentExpense(expense);
    const totalAmount = expense.total_amount || expense.amount || 0;
    const totalPaid = expense.total_paid || 0;
    const balanceDue = totalAmount - totalPaid;
    
    setExpensePaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: balanceDue.toFixed(2),
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
    setExpensePaymentDialog(true);
  };

  const handleCloseExpensePayment = () => {
    setExpensePaymentDialog(false);
    setPaymentExpense(null);
    setExpensePaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleSaveExpensePayment = async () => {
    if (!expensePaymentForm.amount || parseFloat(expensePaymentForm.amount) <= 0) {
      return showSnackbar('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
    }

    try {
      const response = await fetchWithAuth(`/api/general-expenses/${paymentExpense.id}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_date: expensePaymentForm.payment_date,
          amount: parseFloat(expensePaymentForm.amount),
          payment_method: expensePaymentForm.payment_method,
          reference: expensePaymentForm.reference || null,
          notes: expensePaymentForm.notes || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Σφάλμα αποθήκευσης πληρωμής');
      }

      // Invalidate ALL expense queries to refresh data across all filters
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });

      showSnackbar('Η πληρωμή καταχωρήθηκε επιτυχώς!', 'success');
      handleCloseExpensePayment();
      
      // Refresh payment history if it's open
      if (paymentHistoryDialog && paymentHistoryExpense?.id === paymentExpense.id) {
        fetchPaymentHistory(paymentExpense.id);
      }
    } catch (error) {
      showSnackbar(`Σφάλμα: ${error.message}`, 'error');
    }
  };

  // ========== PAYMENT HISTORY HANDLERS ==========
  const fetchPaymentHistory = async (expenseId) => {
    setLoadingPaymentHistory(true);
    try {
      const response = await fetchWithAuth(`/api/general-expenses/${expenseId}/payments`);
      if (!response.ok) throw new Error('Failed to fetch payments');
      const data = await response.json();
      setPaymentHistoryData(data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      showSnackbar('Σφάλμα φόρτωσης ιστορικού πληρωμών', 'error');
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  const handleOpenPaymentHistory = async (expense) => {
    setPaymentHistoryExpense(expense);
    setPaymentHistoryDialog(true);
    await fetchPaymentHistory(expense.id);
  };

  const handleClosePaymentHistory = () => {
    setPaymentHistoryDialog(false);
    setPaymentHistoryExpense(null);
    setPaymentHistoryData([]);
  };

  const handleDeleteExpensePayment = async (paymentId) => {
    if (!window.confirm('Θέλετε σίγουρα να διαγράψετε αυτή την πληρωμή;')) return;
    
    try {
      const response = await fetchWithAuth(`/api/general-expenses/${paymentHistoryExpense.id}/payment/${paymentId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete payment');
      
      showSnackbar('Η πληρωμή διαγράφηκε', 'success');
      
      // Refresh payment history
      await fetchPaymentHistory(paymentHistoryExpense.id);
      
      // Invalidate all expense queries to refresh data across all filters
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    } catch (error) {
      showSnackbar(`Σφάλμα: ${error.message}`, 'error');
    }
  };

  const handleAddPaymentFromHistory = () => {
    // Open payment dialog from history dialog
    handleOpenExpensePayment(paymentHistoryExpense);
  };

  // ========== RECURRING EXPENSES ==========
  // Data is fetched via React Query hooks above

  const handleSaveRecurring = async () => {
    try {
      if (editingRecurring) {
        // Optimistic update for edit
        const currentData = queryClient.getQueryData(['expenses', 'recurring']) || [];
        const recurringIndex = currentData.findIndex(r => r.id === editingRecurring.id);
        
        if (recurringIndex !== -1) {
          const updatedRecurring = {
            ...currentData[recurringIndex],
            ...recurringForm
          };
          
          const updatedData = [...currentData];
          updatedData[recurringIndex] = updatedRecurring;
          
          queryClient.setQueryData(['expenses', 'recurring'], updatedData);
          console.log('✅ Cache updated with edited recurring expense');
        }
        
        await updateRecurringMutation.mutateAsync({
          id: editingRecurring.id,
          data: recurringForm
        });
        showSnackbar('Το επαναλαμβανόμενο έξοδο ενημερώθηκε!', 'success');
      } else {
        const result = await createRecurringMutation.mutateAsync(recurringForm);
        
        // Optimistic update for create
        const currentData = queryClient.getQueryData(['expenses', 'recurring']) || [];
        const newRecurring = {
          id: result.id,
          ...recurringForm
        };
        const updatedData = [...currentData, newRecurring];
        
        queryClient.setQueryData(['expenses', 'recurring'], updatedData);
        console.log('✅ Cache updated with new recurring expense');
        
        showSnackbar('Το επαναλαμβανόμενο έξοδο προστέθηκε!', 'success');
      }
      setRecurringDialog(false);
      setEditingRecurring(null);
      setRecurringForm({
        category_id: '',
        amount: '',
        description: '',
        frequency: 'monthly',
        day_of_month: 1,
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        active: 1
      });
    } catch (error) {
      showSnackbar('Σφάλμα αποθήκευσης επαναλαμβανόμενου εξόδου', 'error');
    }
  };

  const handleToggleRecurring = async (id) => {
    try {
      // Optimistic update for toggle
      const currentData = queryClient.getQueryData(['expenses', 'recurring']) || [];
      const recurringIndex = currentData.findIndex(r => r.id === id);
      
      if (recurringIndex !== -1) {
        const updatedData = [...currentData];
        updatedData[recurringIndex] = {
          ...updatedData[recurringIndex],
          active: updatedData[recurringIndex].active === 1 ? 0 : 1
        };
        
        queryClient.setQueryData(['expenses', 'recurring'], updatedData);
        console.log('✅ Cache updated with toggled recurring expense');
      }
      
      await toggleRecurringMutation.mutateAsync(id);
      showSnackbar('Η κατάσταση ενημερώθηκε!', 'success');
    } catch (error) {
      // On error, refetch to restore correct state
      queryClient.invalidateQueries({ queryKey: ['expenses', 'recurring'] });
      showSnackbar('Σφάλμα ενημέρωσης', 'error');
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το επαναλαμβανόμενο έξοδο;')) return;
    try {
      // Optimistic update for delete
      const currentData = queryClient.getQueryData(['expenses', 'recurring']) || [];
      const updatedData = currentData.filter(r => r.id !== id);
      
      queryClient.setQueryData(['expenses', 'recurring'], updatedData);
      console.log('✅ Cache updated, removed recurring expense:', id);
      
      await deleteRecurringMutation.mutateAsync(id);
      showSnackbar('Το επαναλαμβανόμενο έξοδο διαγράφηκε!', 'success');
    } catch (error) {
      // On error, refetch to restore correct state
      queryClient.invalidateQueries({ queryKey: ['expenses', 'recurring'] });
      showSnackbar('Σφάλμα διαγραφής', 'error');
    }
  };

  // Access denied if user can't view expenses
  if (!canView) {
    return <AccessDenied message="Δεν έχετε πρόσβαση στη διαχείριση εξόδων." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={el}>
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, md: 3 } }}>
        <Box sx={{ mb: { xs: 2.5, md: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)'
            }}>
              <TrendingDown sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Διαχείριση Εξόδων
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Γενικά έξοδα, τιμολόγια & προμηθευτές
              </Typography>
            </Box>
          </Box>
        </Box>

        <Paper sx={{ 
          mb: { xs: 2, md: 3 }, borderRadius: 3, overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, val) => {
              setTabValue(val);
              setSearchParams({ tab: val.toString() });
            }}
            variant="fullWidth"
            sx={{
              bgcolor: 'rgba(0,0,0,0.01)',
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(90deg, #f97316, #ea580c)'
              },
              '& .MuiTab-root': {
                minHeight: { xs: 64, md: 72 },
                fontSize: { xs: '0.65rem', md: '0.875rem' },
                fontWeight: 600,
                minWidth: { xs: 0, md: 120 },
                px: { xs: 0.5, md: 2 },
                py: { xs: 1, md: 1.5 },
                flexDirection: 'column',
                textTransform: 'none',
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  color: '#ea580c',
                  bgcolor: 'rgba(249,115,22,0.04)'
                },
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.02)'
                },
                '& .MuiTab-iconWrapper': {
                  marginBottom: { xs: 0.5, md: 1 },
                  marginRight: 0,
                }
              }
            }}
          >
            <Tab icon={<AccountBalance />} label={isMobile ? "Έξοδα" : "Γενικά Έξοδα"} />
            <Tab icon={<Receipt />} label={isMobile ? "Τιμολόγια" : "Τιμολόγια Προμηθευτών"} />
            <Tab icon={<Business />} label={isMobile ? "Προμηθ." : "Προμηθευτές"} />
            <Tab icon={<Repeat />} label={isMobile ? "Επαναλ." : "Επαναλαμβανόμενα"} />
          </Tabs>
        </Paper>

        {/* ========== GENERAL EXPENSES TAB ========== */}
        <TabPanel value={tabValue} index={0}>
          <GeneralExpensesTab
            generalExpenses={generalExpenses}
            generalStats={generalStats}
            categories={categories}
            startDate={startDate}
            endDate={endDate}
            filterCategory={filterCategory}
            filterStatus={filterStatus}
            generalExpensePage={generalExpensePage}
            generalExpenseRowsPerPage={generalExpenseRowsPerPage}
            generalExpensesPagination={generalExpensesPagination}
            isMobile={isMobile}
            formatCurrency={formatCurrency}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onCategoryChange={setFilterCategory}
            onStatusChange={setFilterStatus}
            onPageChange={(e, newPage) => setGeneralExpensePage(newPage)}
            onRowsPerPageChange={(val) => { setGeneralExpenseRowsPerPage(val); setGeneralExpensePage(0); }}
            onAdd={() => {
              setGeneralExpenseForm({
                category_id: '', amount: '', expense_date: dayjs().format('YYYY-MM-DD'), description: '',
                payment_status: 'pending', payment_date: '', net_amount: '', vat_rate: 0,
                vat_amount: '', total_amount: '', is_recurring: false, day_of_month: '1',
                recurring_type: 'infinite', total_occurrences: ''
              });
              setEditingGeneralExpense(null);
              setGeneralExpenseDialog(true);
            }}
            onEdit={(formData) => { setGeneralExpenseForm(formData); setEditingGeneralExpense(formData); setGeneralExpenseDialog(true); }}
            onDelete={(expense) => handleDeleteGeneralExpense(expense.id)}
            onPayment={handleOpenExpensePayment}
            onOpenPaymentHistory={handleOpenPaymentHistory}
          />
        </TabPanel>
        {/* ========== INVOICES TAB ========== */}
        <TabPanel value={tabValue} index={1}>
          <InvoicesTab
            filteredInvoices={filteredInvoices}
            invoiceStats={invoiceStats}
            categories={categories}
            suppliers={suppliers}
            selectedInvoices={selectedInvoices}
            startDate={startDate}
            endDate={endDate}
            filterSupplier={filterSupplier}
            filterCategory={filterCategory}
            filterStatus={filterStatus}
            invoiceSearchTerm={invoiceSearchTerm}
            invoicePage={invoicePage}
            invoiceRowsPerPage={invoiceRowsPerPage}
            invoicesPagination={invoicesPagination}
            syncing={syncing}
            isMobile={isMobile}
            formatCurrency={formatCurrency}
            navigate={navigate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSupplierChange={setFilterSupplier}
            onCategoryChange={setFilterCategory}
            onStatusChange={setFilterStatus}
            onSearchChange={setInvoiceSearchTerm}
            onPageChange={(e, newPage) => setInvoicePage(newPage)}
            onRowsPerPageChange={(e) => { setInvoiceRowsPerPage(parseInt(e.target.value, 10)); setInvoicePage(0); }}
            onAdd={() => {
              setInvoiceForm({
                supplier_id: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0],
                amount: '', category_id: '', description: '', payment_status: 'pending',
                payment_date: '', payment_terms: 'credit', net_amount: '', vat_rate: 24,
                vat_amount: '', total_amount: ''
              });
              setEditingInvoice(null);
              setInvoiceDialog(true);
            }}
            onEdit={(invoice) => {
              const formData = {
                ...invoice,
                net_amount: invoice.net_amount || (invoice.amount / 1.24).toFixed(2),
                vat_rate: invoice.vat_rate || 24,
                vat_amount: invoice.vat_amount || (invoice.amount - (invoice.amount / 1.24)).toFixed(2),
                total_amount: invoice.total_amount || invoice.amount
              };
              setInvoiceForm(formData);
              setEditingInvoice(invoice);
              setInvoiceDialog(true);
            }}
            onDelete={(id) => handleDeleteInvoice(id)}
            onPayment={handleOpenInvoicePayment}
            onMarkPaid={handleMarkInvoicePaid}
            onOpenPreview={handleOpenInvoicePreview}
            onSelectInvoice={handleSelectInvoice}
            onSelectAllInvoices={handleSelectAllInvoices}
            onBulkDelete={handleBulkDeleteInvoices}
            onSyncMydata={handleSyncMydata}
          />
        </TabPanel>

        {/* ========== SUPPLIERS TAB ========== */}
        <TabPanel value={tabValue} index={2}>
          <SuppliersTab
            suppliers={filteredSuppliers}
            supplierCategories={supplierCategories}
            selectedSuppliers={selectedSuppliers}
            supplierSearchTerm={supplierSearchTerm}
            filterSupplierCategory={filterSupplierCategory}
            supplierPeriod={supplierPeriod}
            filterSupplierBalance={filterSupplierBalance}
            filterSupplierTurnover={filterSupplierTurnover}
            supplierSort={supplierSort}
            suppliersSummary={suppliersSummary}
            formatCurrency={formatCurrency}
            syncing={syncing}
            isMobile={isMobile}
            suppliersPagination={suppliersPagination}
            supplierPage={supplierPage}
            supplierRowsPerPage={supplierRowsPerPage}
            navigate={navigate}
            onSearchChange={(v) => { setSupplierSearchTerm(v); setSupplierPage(0); }}
            onCategoryFilterChange={(v) => { setFilterSupplierCategory(v); setSupplierPage(0); }}
            onPeriodChange={(v) => { setSupplierPeriod(v); setSupplierPage(0); }}
            onBalanceFilterChange={(v) => { setFilterSupplierBalance(v); setSupplierPage(0); }}
            onTurnoverFilterChange={(v) => { setFilterSupplierTurnover(v); setSupplierPage(0); }}
            onSortChange={(v) => { setSupplierSort(v); setSupplierPage(0); }}
            onSyncGemi={handleUpdateSupplierNames}
            onAdd={() => {
              setSupplierForm({ name: '', vat_number: '', phone: '', email: '', address: '', notes: '', payment_terms: 'credit', category_id: '' });
              setEditingSupplier(null);
              setSupplierDialog(true);
            }}
            onEdit={(s) => { setSupplierForm(s); setEditingSupplier(s); setSupplierDialog(true); }}
            onDelete={(s) => handleDeleteSupplier(s.id)}
            onSelectSupplier={handleSelectSupplier}
            onSelectAllSuppliers={handleSelectAllSuppliers}
            onBulkDelete={handleBulkDeleteSuppliers}
            onPageChange={(e, p) => setSupplierPage(p)}
            onRowsPerPageChange={(e) => { setSupplierRowsPerPage(parseInt(e.target.value, 10)); setSupplierPage(0); }}
          />
        </TabPanel>

        {/* ========== RECURRING EXPENSES TAB ========== */}
        <TabPanel value={tabValue} index={3}>
          <RecurringExpensesTab
            recurringExpenses={recurringExpenses}
            categories={categories}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            onAdd={() => {
              setRecurringForm({ category_id: '', amount: '', description: '', frequency: 'monthly', day_of_month: 1, start_date: new Date().toISOString().split('T')[0], end_date: '', active: 1 });
              setEditingRecurring(null);
              setRecurringDialog(true);
            }}
            onEdit={(r) => { setRecurringForm(r); setEditingRecurring(r); setRecurringDialog(true); }}
            onDelete={handleDeleteRecurring}
            onToggle={handleToggleRecurring}
          />
        </TabPanel>


        {/* ========== ALL DIALOGS ========== */}
        <ExpenseDialogs
          generalExpenseDialog={generalExpenseDialog}
          setGeneralExpenseDialog={setGeneralExpenseDialog}
          editingGeneralExpense={editingGeneralExpense}
          generalExpenseForm={generalExpenseForm}
          setGeneralExpenseForm={setGeneralExpenseForm}
          handleSaveGeneralExpense={handleSaveGeneralExpense}
          invoiceDialog={invoiceDialog}
          setInvoiceDialog={setInvoiceDialog}
          editingInvoice={editingInvoice}
          invoiceForm={invoiceForm}
          setInvoiceForm={setInvoiceForm}
          handleSaveInvoice={handleSaveInvoice}
          supplierDialog={supplierDialog}
          setSupplierDialog={setSupplierDialog}
          editingSupplier={editingSupplier}
          supplierForm={supplierForm}
          setSupplierForm={setSupplierForm}
          handleSaveSupplier={handleSaveSupplier}
          recurringDialog={recurringDialog}
          setRecurringDialog={setRecurringDialog}
          editingRecurring={editingRecurring}
          recurringForm={recurringForm}
          setRecurringForm={setRecurringForm}
          handleSaveRecurring={handleSaveRecurring}
          invoicePreviewDialog={invoicePreviewDialog}
          setInvoicePreviewDialog={setInvoicePreviewDialog}
          invoicePreviewData={invoicePreviewData}
          invoicePreviewLoading={invoicePreviewLoading}
          invoicePaymentDialog={invoicePaymentDialog}
          setInvoicePaymentDialog={setInvoicePaymentDialog}
          paymentInvoice={paymentInvoice}
          invoicePaymentForm={invoicePaymentForm}
          setInvoicePaymentForm={setInvoicePaymentForm}
          handleSaveInvoicePayment={handleSaveInvoicePayment}
          handleCloseInvoicePayment={() => { setInvoicePaymentDialog(false); setPaymentInvoice(null); }}
          expensePaymentDialog={expensePaymentDialog}
          setExpensePaymentDialog={setExpensePaymentDialog}
          paymentExpense={paymentExpense}
          expensePaymentForm={expensePaymentForm}
          setExpensePaymentForm={setExpensePaymentForm}
          handleSaveExpensePayment={handleSaveExpensePayment}
          handleCloseExpensePayment={() => { setExpensePaymentDialog(false); setPaymentExpense(null); }}
          paymentHistoryDialog={paymentHistoryDialog}
          setPaymentHistoryDialog={setPaymentHistoryDialog}
          paymentHistoryData={paymentHistoryData}
          loadingPaymentHistory={loadingPaymentHistory}
          paymentHistoryExpense={paymentHistoryExpense}
          handleClosePaymentHistory={() => setPaymentHistoryDialog(false)}
          handleAddPaymentFromHistory={() => {
            setPaymentHistoryDialog(false);
            if (paymentHistoryExpense) handleOpenExpensePayment(paymentHistoryExpense);
          }}
          handleDeleteExpensePayment={handleDeleteExpensePayment}
          categories={categories}
          suppliers={suppliers}
          supplierCategories={supplierCategories}
          formatCurrency={formatCurrency}
          navigate={navigate}
          isMobile={isMobile}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}
